import isEqual from 'lodash/isEqual';
import { FormEntry } from '@ynomia/dynamic-form';
import { models } from '@ynomia/core';
import { PRESET_TWIN_ID_NAME } from '../../../config/constants';
import {
  MAPPING_FILTER_EXAMPLE,
  areMappingFiltersValid,
  parseMappingFilters,
} from './mappingFilters';

/** For a new model, and for an existing one saved before the option existed. */
export const DEFAULT_MAP_LARGEST_ELEMENT_ONLY = true;

/** The location twin ID key in any project with a precast asset type. */
export const PRECAST_TWIN_ID_FIELD = 'twinID';

const findPrecastAssetType = (assetTypes: Array<{ id: string }>) => (
  assetTypes.find(t => t.id.endsWith('::precast'))?.id
);

/** The form fields that make up a model's mapping settings. */
export type MappingFormValues = {
  twinIdField?: string,
  mappingFilters?: string,
  mapLargestElementOnly?: boolean,
  isolationAssetType?: string,
  isolationTwinIdField?: string,
};

/**
 * The asset type whose destinations locate this model's elements: precast where the project has
 * it, otherwise the model's first asset type.
 */
export const defaultIsolationAssetType = (
  assetTypes: Array<{ id: string }>,
  modelAssetTypes: Array<string> = [],
): string | undefined => (
  findPrecastAssetType(assetTypes) ?? modelAssetTypes[0]
);

/** `twinID` where the project has a precast asset type, otherwise the model's own key. */
export const defaultIsolationTwinIdField = (
  assetTypes: Array<{ id: string }>,
  twinIdField?: string,
): string | undefined => (
  findPrecastAssetType(assetTypes) ? PRECAST_TWIN_ID_FIELD : twinIdField
);

/** A model's saved settings, as form values. */
export const mappingFormValues = (model: Partial<models.Model>): MappingFormValues => ({
  twinIdField: model.twinIdField,
  mappingFilters: model.mappingConfig?.ignoreRules?.length
    ? JSON.stringify(model.mappingConfig.ignoreRules, null, 2)
    : '',
  mapLargestElementOnly: model.mappingConfig?.mapLargestElementOnly
    ?? DEFAULT_MAP_LARGEST_ELEMENT_ONLY,
  isolationAssetType: model.isolationMappingConfig?.assetType,
  isolationTwinIdField: model.isolationMappingConfig?.twinIdField,
});

/**
 * Form values back into the config the mapping function reads. A blank location asset type or
 * twin ID key falls back to its default, so the saved config is always complete.
 */
export const mappingConfigFromForm = (
  values: MappingFormValues,
  assetTypes: Array<{ id: string }>,
  modelAssetTypes: Array<string>,
): Pick<models.Model, 'mappingConfig' | 'isolationMappingConfig'> => {
  const isolationAssetType = values.isolationAssetType
    || defaultIsolationAssetType(assetTypes, modelAssetTypes);
  return {
    mappingConfig: {
      ignoreRules: parseMappingFilters(values.mappingFilters || ''),
      mapLargestElementOnly: !!values.mapLargestElementOnly,
    },
    ...(isolationAssetType ? {
      isolationMappingConfig: {
        assetType: isolationAssetType,
        twinIdField: values.isolationTwinIdField
          || defaultIsolationTwinIdField(assetTypes, values.twinIdField)!,
      },
    } : {}),
  };
};

export const mappingFiltrexFunctions = {
  isValidMappingFilters: (str?: string) => areMappingFiltersValid(str || ''),
};

export const mappingFormFields = (
  assetTypes: Array<{ id: string, label: string }>,
): Array<FormEntry> => [
  {
    entryComponent: 'checkbox',
    id: 'mapLargestElementOnly',
    label: 'Auto Ignore Minor Elements',
  },
  {
    entryComponent: 'textarea',
    id: 'mappingFilters',
    label: 'Ignore Elements (Manual)',
    properties: {
      placeholder: MAPPING_FILTER_EXAMPLE,
      autoSize: { minRows: 1, maxRows: 8 },
    },
    validationConditions: [{
      condition: 'not isValidMappingFilters(mappingFilters of values)',
      message: 'Must be a JSON array of { "key": "...", "value": "<regular expression>" } objects',
    }],
  },
  {
    entryComponent: 'collapse',
    id: 'showLocationMapping',
    label: 'Location Mapping (Advanced)',
  },
  {
    entryComponent: 'picklist',
    id: 'isolationAssetType',
    show: 'showLocationMapping of values',
    allowHiddenSubmit: true,
    label: 'Location Asset Type',
    properties: {
      options: assetTypes.map(assetType => ({
        label: assetType.label,
        value: assetType.id,
      })),
    },
  },
  {
    entryComponent: 'picklist',
    id: 'isolationTwinIdField',
    show: 'showLocationMapping of values',
    allowHiddenSubmit: true,
    label: 'Twin ID Property Key',
    properties: {
      placeholder: findPrecastAssetType(assetTypes)
        ? PRECAST_TWIN_ID_FIELD
        : '(Same as the Twin ID Property Key)',
      options: PRESET_TWIN_ID_NAME.map(label => ({ label, value: label })),
      tags: true,
      maxCount: 1,
    },
  },
] as Array<FormEntry>;

/** Whether an update changes anything the mapping is built from. */
export const affectsMapping = (
  model: Partial<models.Model>,
  update: Partial<models.Model>,
): boolean => (
  (update.twinIdField !== undefined && update.twinIdField !== model.twinIdField)
  || (update.mappingConfig !== undefined && !isEqual(update.mappingConfig, model.mappingConfig))
  || (update.isolationMappingConfig !== undefined
    && !isEqual(update.isolationMappingConfig, model.isolationMappingConfig))
);
